const chunkSize = 8 * 1024 * 1024;

// Hash every byte without keeping the whole file in browser memory. The chained
// chunk digest is an identity check for reselecting an interrupted upload, not a
// replacement for the storage provider's checksum.
export async function fingerprintImportFile(file: Blob, signal: AbortSignal): Promise<string> {
    let digest = new Uint8Array(32);
    for (let offset = 0; offset < file.size; offset += chunkSize) {
        signal.throwIfAborted();
        const chunk = await file.slice(offset, offset + chunkSize).arrayBuffer();
        const chunkDigest = new Uint8Array(await crypto.subtle.digest('SHA-256', chunk));
        const combined = new Uint8Array(64);
        combined.set(digest);
        combined.set(chunkDigest, 32);
        digest = new Uint8Array(await crypto.subtle.digest('SHA-256', combined));
    }
    signal.throwIfAborted();
    return `${file.size}:${Array.from(digest, byte => byte.toString(16).padStart(2, '0')).join('')}`;
}

export async function uploadImportFile(file: Blob, uploadUrl: string, signal: AbortSignal, onProgress: (bytes: number) => void): Promise<void> {
    // Never include the resumable credential in thrown errors or telemetry.
    const request = async (body: Blob | undefined, range: string) => {
        try {
            return await fetch(uploadUrl, {
                method: 'PUT', body, signal,
                headers: { 'Content-Range': range, 'Content-Type': file.type || 'application/octet-stream' },
            });
        } catch {
            signal.throwIfAborted();
            throw new Error('File transfer interrupted. Reselect the same file to resume.');
        }
    };
    const nextOffset = (response: Response) => {
        if (response.ok) return file.size;
        if (response.status !== 308) throw new Error('File transfer failed. Reselect the same file to retry.');
        const range = response.headers.get('Range');
        if (!range) return 0;
        const match = /^bytes=0-(\d+)$/.exec(range);
        if (!match) throw new Error('Could not confirm the uploaded bytes. Please retry.');
        const offset = Number(match[1]) + 1;
        if (!Number.isSafeInteger(offset) || offset > file.size) throw new Error('The uploaded byte count does not match this file. Please retry.');
        return offset;
    };
    let offset = nextOffset(await request(undefined, `bytes */${file.size}`));
    onProgress(offset);
    let retries = 0;
    while (offset < file.size) {
        signal.throwIfAborted();
        const end = Math.min(offset + chunkSize, file.size);
        let response: Response;
        try {
            response = await request(file.slice(offset, end), `bytes ${offset}-${end - 1}/${file.size}`);
        } catch (error) {
            if (signal.aborted || ++retries > 3) throw error;
            response = await request(undefined, `bytes */${file.size}`);
        }
        if (response.status >= 500 && ++retries <= 3) response = await request(undefined, `bytes */${file.size}`);
        const confirmedOffset = nextOffset(response);
        if (confirmedOffset <= offset && ++retries > 3) throw new Error('File transfer stopped making progress. Please retry.');
        offset = confirmedOffset;
        onProgress(offset);
    }
}

// The last PUT may have succeeded even when its response or the subsequent
// acknowledgement was lost. Confirm completion without asking for the file again.
export async function isImportUploadComplete(byteSize: number, uploadUrl: string, signal: AbortSignal): Promise<boolean> {
    let response: Response;
    try {
        response = await fetch(uploadUrl, { method: 'PUT', signal, headers: { 'Content-Range': `bytes */${byteSize}` } });
    } catch {
        signal.throwIfAborted();
        throw new Error('Could not check the file transfer. Please retry.');
    }
    if (response.ok) return true;
    if (response.status === 308) return false;
    throw new Error('Could not confirm the file transfer. Please retry.');
}
